import React, { useCallback, useEffect, useMemo } from 'react'
import { MenuItem } from '@mui/material'
import { string } from 'yup'

import { useDispatch, useSelector } from '@tabeeb/store'
import { getFoldersList } from '@tabeeb/modules/gallery/selectors'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { TextField, Stack } from '@tabeeb/uikit'

import { DEFAULT_FOLDER_ID } from '../../services/constants'
import usePrepareCreateSplatModel from '../../hooks/usePrepareCreateSplatModel'

interface GeneralStepProps {
  pageName: string
  folderId: number
  pageIds: number[]
  defaultSettingsLoaded: boolean
  setPageName: (value: string) => void
  setFolderId: (value: number) => void
  setIsNextStepDisabled: (value: boolean) => void
  setDefaultSettingsLoaded: (value: boolean) => void
}

const pageNameSchema = string()
  .trim('There should be no spaces at the beginning and at the end')
  .strict(true)
  .required('Page name is required')
  .max(50, 'Page name must be at most 50 characters long')

const GeneralStep: React.FC<GeneralStepProps> = ({
  pageIds,
  pageName,
  setPageName,
  folderId,
  setFolderId,
  setIsNextStepDisabled,
  defaultSettingsLoaded,
  setDefaultSettingsLoaded,
}) => {
  const dispatch = useDispatch()

  const enabled = useMemo(() => !defaultSettingsLoaded && pageIds.length > 0, [defaultSettingsLoaded, pageIds.length])

  const onError = useCallback(() => {
    dispatch(onAddErrorNotification({ message: 'Failed to get default SPLAT model settings' }))
  }, [dispatch])

  const { splatModel, loaded, loading } = usePrepareCreateSplatModel({
    pageIds,
    enabled,
    onError,
  })

  const { validationMessage, isNameInvalid } = useMemo(() => {
    try {
      pageNameSchema.validateSync(pageName)
      return { validationMessage: '', isNameInvalid: false }
    } catch (e: any) {
      return { validationMessage: e.message || '', isNameInvalid: true }
    }
  }, [pageName])

  const foldersList = useSelector(getFoldersList) as { Id: number; Name: string }[]

  const isError = useMemo(() => isNameInvalid && !loading, [isNameInvalid, loading])

  useEffect(() => {
    setIsNextStepDisabled(loading || isNameInvalid)
  }, [isNameInvalid, loading, setIsNextStepDisabled])

  useEffect(() => {
    if (loaded && splatModel) {
      setPageName(splatModel.PageName ?? '')
      setFolderId(splatModel.FolderId ?? DEFAULT_FOLDER_ID)
      setDefaultSettingsLoaded(true)
    }
  }, [loaded, splatModel, setDefaultSettingsLoaded, setFolderId, setPageName])

  return (
    <Stack spacing={2}>
      <TextField
        value={pageName}
        label='Page Name'
        onChange={(e) => setPageName(e.target.value)}
        size='small'
        name='pageName'
        disabled={loading}
        fullWidth
        error={isError}
        helperText={isError ? validationMessage : ''}
      />
      {foldersList && foldersList.length > 0 && (
        // TODO: Use FormikSelect if Formik is used in the future
        <TextField
          select
          fullWidth
          size='small'
          value={folderId}
          onChange={(e) => setFolderId(Number(e.target.value))}
          disabled={loading}
          label='Destination Folder'
        >
          <MenuItem value={DEFAULT_FOLDER_ID}>Place in the Gallery</MenuItem>
          {foldersList.map((folder) => (
            <MenuItem value={folder.Id} key={folder.Id}>
              {folder.Name}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  )
}

export default GeneralStep
